import { useState, useCallback, useMemo } from 'react';
import { transpose } from '@hrgui/chord-charts';

export type TransposeSettings = {
    transposeNumber: number;
};

export type ChordLyricsData = {
    lyrics: string;
    transposeSettings?: TransposeSettings;
};

export const parseChordLyricsData = (value: string | undefined): ChordLyricsData => {
    if (!value || value === '') {
        return { lyrics: '', transposeSettings: { transposeNumber: 0 } };
    }

    try {
        // Try parsing as JSON first (new format)
        const parsed = JSON.parse(value);

        // Check if it's the new format with lyrics property
        if (typeof parsed === 'object' && parsed !== null && 'lyrics' in parsed) {
            return {
                lyrics: parsed.lyrics || '',
                transposeSettings: parsed.transposeSettings || { transposeNumber: 0 }
            };
        }

        // Otherwise, treat the parsed value as legacy data
        return {
            lyrics: String(parsed),
            transposeSettings: { transposeNumber: 0 }
        };
    } catch {
        // If JSON parsing fails, treat as legacy plain text
        return {
            lyrics: value,
            transposeSettings: { transposeNumber: 0 }
        };
    }
};

export const parseTransposeSettings = (value: string | undefined): TransposeSettings => {
    if (!value || value === '') {
        return { transposeNumber: 0 };
    }

    try {
        // Try parsing as JSON first
        const parsed = JSON.parse(value);

        // Check if it's an object with the expected structure
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
            return {
                transposeNumber: parsed.transposeNumber ?? 0
            };
        }

        // If it's a number (legacy format), use it directly
        if (typeof parsed === 'number') {
            return {
                transposeNumber: parsed
            };
        }

        // Otherwise, return defaults
        return { transposeNumber: 0 };
    } catch {
        // If JSON parsing fails completely, try parseInt as fallback
        const num = parseInt(value, 10);
        return {
            transposeNumber: isNaN(num) ? 0 : num
        };
    }
};

// Chromatic scale (sharps)
export const CHROMATIC_KEYS_SHARPS = [
    'C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'
];

interface UseChordTransposeOptions {
    initialSettings?: TransposeSettings;
    onSave?: (settings: TransposeSettings) => Promise<void>;
    isEditing?: boolean;
}

interface UseChordTransposeReturn {
    transposeSettings: TransposeSettings;
    targetKey: string;
    transposedContent: string;
    handleTranspose: (steps: number) => Promise<void>;
    resetTranspose: () => Promise<void>;
    setTransposeSettings: (settings: TransposeSettings) => void;
}

export const useChordTranspose = (
    content: string,
    options: UseChordTransposeOptions = {}
): UseChordTransposeReturn => {
    const {
        initialSettings = { transposeNumber: 0 },
        onSave,
        isEditing = false
    } = options;

    const [transposeSettings, setTransposeSettings] = useState<TransposeSettings>(initialSettings);
    const { transposeNumber } = transposeSettings;

    // Calculate the target key based on transpose steps (always use sharps)
    const targetKey = useMemo(() => {
        if (transposeNumber === 0) return 'C';

        // Calculate new index with wrapping (C is at index 0)
        const newIndex = (transposeNumber + 12) % 12;
        return CHROMATIC_KEYS_SHARPS[newIndex];
    }, [transposeNumber]);

    // Get transposed content
    const transposedContent = useMemo(() => {
        if (!content.trim() || transposeNumber === 0) {
            return content;
        }

        try {
            return transpose(content, 'C', targetKey);
        } catch (error) {
            // If transpose fails, fallback to original content
            return content;
        }
    }, [content, transposeNumber, targetKey]);

    const handleTranspose = useCallback(async (steps: number) => {
        const newSteps = Math.max(-11, Math.min(11, transposeNumber + steps));
        const newSettings = { ...transposeSettings, transposeNumber: newSteps };
        setTransposeSettings(newSettings);
        
        // Save immediately if not in editing mode
        if (!isEditing && onSave) {
            await onSave(newSettings);
        }
    }, [transposeNumber, transposeSettings, isEditing, onSave]);

    const resetTranspose = useCallback(async () => {
        const newSettings = { ...transposeSettings, transposeNumber: 0 };
        setTransposeSettings(newSettings);
        
        // Save immediately if not in editing mode
        if (!isEditing && onSave) {
            await onSave(newSettings);
        }
    }, [transposeSettings, isEditing, onSave]);

    return {
        transposeSettings,
        targetKey,
        transposedContent,
        handleTranspose,
        resetTranspose,
        setTransposeSettings,
    };
};